import { useAuth } from '../contexts/AuthContext';
import { TFunction } from 'i18next';

export interface PdfConversionStatus {
    available: boolean;
    methods: string[];
    recommended: string | null;
}

/**
 * The browser would not let the page print the contract PDF itself: the hidden frame holding it
 * is not scriptable, typically because a PDF extension took the document over onto its own origin
 * (Sentry, PROD, 2026-11-02: `SecurityError` setting `onafterprint`). The rendered document is
 * carried along so the caller can show it, and the viewer's own print button still works.
 */
export class ContractPrintBlockedError extends Error {
    readonly blob: Blob;
    /** What the browser threw when the frame was scripted. */
    readonly reason: unknown;

    constructor(blob: Blob, reason: unknown) {
        super('The browser blocked printing the contract document directly');
        this.name = 'ContractPrintBlockedError';
        this.blob = blob;
        this.reason = reason;
    }
}

/**
 * Whether this browser displays a PDF inside the page. Chrome reports `false` when it is set to
 * download PDFs instead of opening them, and so does a browser with no viewer at all; a frame
 * holding a PDF then downloads it and never loads. A browser that predates the property is taken
 * to have a viewer, as every one this application supports does.
 */
export const browserShowsPdfs = (): boolean =>
    typeof navigator === 'undefined' || navigator.pdfViewerEnabled !== false;

/**
 * How long the hidden print frame may take to load the PDF. Chrome's viewer loads a contract in
 * well under a second; a frame that has not loaded by then never will.
 */
export const PRINT_FRAME_LOAD_TIMEOUT_MS = 15000;

/**
 * Prints a blob through a hidden iframe and the browser print dialog. Resolves once the dialog
 * has been asked for; rejects with ContractPrintBlockedError, after removing the frame, when the
 * frame cannot be scripted or never loads, so a blocked print is never reported as sent.
 *
 * A frame that never loads is a browser with no PDF viewer in it - Chrome set to download PDFs
 * instead of opening them, or Playwright's headless shell - where the PDF is downloaded and the
 * old code reported it as printed.
 */
const printBlob = (blob: Blob): Promise<void> =>
    new Promise((resolve, reject) => {
        if (!browserShowsPdfs()) {
            // Chrome set to download PDFs (or a browser without a viewer): the frame would only
            // download the file and never load, so there is nothing to wait for.
            reject(new ContractPrintBlockedError(blob, new Error('This browser does not display PDFs')));
            return;
        }
        const blobUrl = window.URL.createObjectURL(blob);
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.setAttribute('aria-hidden', 'true');

        const cleanup = (): void => {
            window.URL.revokeObjectURL(blobUrl);
            if (iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
            }
        };

        let settled = false;
        const block = (reason: unknown): void => {
            settled = true;
            cleanup();
            reject(new ContractPrintBlockedError(blob, reason));
        };
        const loadTimer = setTimeout(
            () => block(new Error('The print frame did not load')),
            PRINT_FRAME_LOAD_TIMEOUT_MS,
        );

        iframe.onload = (): void => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(loadTimer);
            const printWindow = iframe.contentWindow;
            try {
                if (!printWindow) {
                    throw new Error('The print frame has no window');
                }
                // Remove the iframe once the print dialog has been dismissed
                printWindow.onafterprint = cleanup;
                printWindow.focus();
                printWindow.print();
            } catch (error) {
                block(error);
                return;
            }
            resolve();
        };

        iframe.src = blobUrl;
        document.body.appendChild(iframe);
    });

/** The file name a contract document is saved under: dots and slashes of the number become `_`. */
export const contractFileName = (
    contractNumber: string | null | undefined,
    extension: 'pdf' | 'docx',
): string =>
    `Contract_${contractNumber ? String(contractNumber).replace(/[./]/g, '_') : 'unknown'}.${extension}`;

export type ProgressCallback = (
    progress: number,
    status: 'preparing' | 'processing' | 'completed' | 'error',
    message?: string,
) => void;

// Hook for contract document operations
export const useContractDocument = (t: TFunction) => {
    const { API_BASE_URL, token } = useAuth();

    // Helper function to make authenticated fetch requests
    const makeAuthenticatedRequest = async (url: string, errorMessage: string): Promise<Blob> => {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                ...(token && { Authorization: `Bearer ${token}` }),
            },
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message ?? `${errorMessage}: ${response.status}`);
        }

        return response.blob();
    };

    // Helper function to make authenticated fetch requests with progress
    const makeAuthenticatedRequestWithProgress = async (
        url: string,
        errorMessage: string,
        onProgress?: ProgressCallback,
    ): Promise<Blob> => {
        if (onProgress) {
            onProgress(40, 'processing', t('progress.requestingDocument'));
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                ...(token && { Authorization: `Bearer ${token}` }),
            },
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message ?? `${errorMessage}: ${response.status}`);
        }

        if (onProgress) {
            onProgress(70, 'processing', t('progress.downloadingDocument'));
        }

        return response.blob();
    };

    // Helper function to download blob as file
    const downloadBlob = (blob: Blob, filename: string): void => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;

        // Trigger download
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const downloadDocument = async (contractId: number, contractNumber: string): Promise<void> => {
        try {
            const blob = await makeAuthenticatedRequest(
                `${API_BASE_URL}/contracts/${contractId}/document`,
                'Failed to generate contract document',
            );

            const filename = contractFileName(contractNumber, 'docx');
            downloadBlob(blob, filename);
        } catch (error) {
            console.error('Error downloading contract document:', error);
            throw error;
        }
    };

    /**
     * Shared prologue of the two PDF actions: reports progress, paces the UI and
     * resolves to the rendered PDF. Download and print need this identical
     * sequence and only diverge once the blob is in hand.
     */
    const fetchPdfWithProgress = async (
        contractId: number,
        onProgress?: ProgressCallback,
    ): Promise<Blob> => {
        // Start with preparing status
        if (onProgress) {
            onProgress(10, 'preparing', t('progress.initializing'));
        }

        // Simulate some preparation time
        await new Promise(resolve => setTimeout(resolve, 500));

        if (onProgress) {
            onProgress(30, 'processing', t('progress.generatingPdf'));
        }

        const blob = await makeAuthenticatedRequestWithProgress(
            `${API_BASE_URL}/contracts/${contractId}/document/pdf`,
            'Failed to generate PDF contract document',
            onProgress,
        );

        if (onProgress) {
            onProgress(80, 'processing', t('progress.finalizingDocument'));
        }

        // Simulate finalization
        await new Promise(resolve => setTimeout(resolve, 300));

        return blob;
    };

    const downloadPdfDocument = async (
        contractId: number,
        contractNumber: string,
        onProgress?: ProgressCallback,
    ): Promise<void> => {
        try {
            const blob = await fetchPdfWithProgress(contractId, onProgress);

            const filename = contractFileName(contractNumber, 'pdf');
            downloadBlob(blob, filename);

            if (onProgress) {
                onProgress(100, 'completed', 'PDF document ready!');
            }
        } catch (error) {
            console.error('Error downloading PDF contract document:', error);
            if (onProgress) {
                onProgress(0, 'error', 'Failed to generate PDF document');
            }
            throw error;
        }
    };

    const printPdfDocument = async (
        contractId: number,
        onProgress?: ProgressCallback,
    ): Promise<void> => {
        try {
            const blob = await fetchPdfWithProgress(contractId, onProgress);

            await printBlob(blob);

            if (onProgress) {
                onProgress(100, 'completed', t('progress.sentToPrinter'));
            }
        } catch (error) {
            if (error instanceof ContractPrintBlockedError) {
                // Not a failure of the document: the caller shows it for the viewer to print.
                console.warn('Printing the contract directly was blocked:', error.reason);
                if (onProgress) {
                    onProgress(100, 'completed', t('progress.openedForPrinting'));
                }
                throw error;
            }
            console.error('Error printing PDF contract document:', error);
            if (onProgress) {
                onProgress(0, 'error', t('progress.printError'));
            }
            throw error;
        }
    };

    /**
     * Renders the contract PDF and resolves to an object URL an on-screen viewer can show;
     * the caller revokes it once the viewer closes.
     */
    const previewPdfDocument = async (contractId: number): Promise<string> => {
        try {
            const blob = await makeAuthenticatedRequest(
                `${API_BASE_URL}/contracts/${contractId}/document/pdf`,
                'Failed to generate PDF contract document',
            );
            return window.URL.createObjectURL(blob);
        } catch (error) {
            console.error('Error previewing PDF contract document:', error);
            throw error;
        }
    };

    const visualizeDocument = async (contractId: number): Promise<string> => {
        try {
            const blob = await makeAuthenticatedRequest(
                `${API_BASE_URL}/contracts/${contractId}/document`,
                'Failed to generate contract document',
            );

            // Create object URL for visualization
            return window.URL.createObjectURL(blob);
        } catch (error) {
            console.error('Error visualizing contract document:', error);
            throw error;
        }
    };

    const checkPdfConversionStatus = async (): Promise<PdfConversionStatus> => {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/pdf-conversion/status`, {
                method: 'GET',
                headers: {
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
                credentials: 'include',
            });

            if (!response.ok) {
                console.warn('Failed to check PDF conversion status');
                return { available: false, methods: [], recommended: null };
            }

            return await response.json();
        } catch (error) {
            console.warn('Error checking PDF conversion status:', error);
            return { available: false, methods: [], recommended: null };
        }
    };

    return {
        downloadDocument,
        downloadPdfDocument,
        printPdfDocument,
        previewPdfDocument,
        visualizeDocument,
        checkPdfConversionStatus,
    };
};
